import { reportUsage, reportUsageNow } from '@ms/yammer-telemetry';

import type { EcsConfig, EcsProjectTeamName } from './ecsConfig';
import type { Experiment, ExperimentTreatmentType, ExperimentTreatmentValue } from './experiments';
import { experimentConfigs } from './experiments';
import { selectExperimentTreatment, selectShouldReportExperimentTreatedEvent } from './selectors';
import type { ExperimentsState } from './store';
import { experimentsStore } from './store';

export type ReportEcsEtagMapping = (ecsConfig: EcsConfig) => void;

export const reportEcsEtagMapping: ReportEcsEtagMapping = ({ configIds, etag }) => {
  reportUsage({
    eventName: 'ecs_etag_mapping',
    eventProperties: {
      ConfigIDs: JSON.stringify(configIds),
      ETag: etag,
    },
  });
};

const reportedExperimentTreatments = new Set<Experiment>();

interface ExperimentTreatmentReportingSnapshot {
  readonly serviceTreatment: ExperimentTreatmentValue | undefined;
  readonly overrideTreatment: ExperimentTreatmentValue | undefined;
  readonly etag: EcsConfig['etag'];
}

export const selectExperimentTreatmentReportingSnapshot = (
  state: ExperimentsState,
  experiment: Experiment
): ExperimentTreatmentReportingSnapshot => ({
  serviceTreatment: state.experimentTreatmentsFromService[experiment],
  overrideTreatment: state.experimentTreatmentOverrides[experiment],
  etag: state.ecsConfig.etag,
});

const claimExperimentTreatmentEvent = (experiment: Experiment): boolean => {
  if (reportedExperimentTreatments.has(experiment)) {
    return false;
  }

  reportedExperimentTreatments.add(experiment);

  return true;
};

export type ReportEcsExperimentTreatment = (
  experiment: Experiment,
  treatment: ExperimentTreatmentValue,
  projectTeam: EcsProjectTeamName,
  etag: EcsConfig['etag']
) => void;

export const reportEcsExperimentTreatment: ReportEcsExperimentTreatment = (
  experiment,
  treatment,
  projectTeam,
  etag
) => {
  if (!claimExperimentTreatmentEvent(experiment)) {
    return;
  }

  reportUsage({
    eventName: 'ecs_experiment_treatment',
    eventProperties: { ProjectTeam: projectTeam, TreatmentKey: experiment, TreatmentValue: treatment, ETag: etag },
  });
};

export type ReportEcsExperimentTreatmentNow = ReportEcsExperimentTreatment;

export const reportEcsExperimentTreatmentNow: ReportEcsExperimentTreatmentNow = (
  experiment,
  treatment,
  projectTeam,
  etag
) => {
  if (!claimExperimentTreatmentEvent(experiment)) {
    return;
  }

  void reportUsageNow({
    eventName: 'ecs_experiment_treatment',
    eventProperties: { ProjectTeam: projectTeam, TreatmentKey: experiment, TreatmentValue: treatment, ETag: etag },
  });
};

export const reportExperimentTreatmentFromSnapshot = (
  experiment: Experiment,
  { serviceTreatment, overrideTreatment, etag }: ExperimentTreatmentReportingSnapshot
): void => {
  if (serviceTreatment === undefined || (overrideTreatment !== undefined && overrideTreatment !== serviceTreatment)) {
    return;
  }

  reportEcsExperimentTreatment(experiment, serviceTreatment, experimentConfigs[experiment].projectTeam, etag);
};

export const getAndReportExperiment = <E extends Experiment>(
  experiment: E,
  state: ExperimentsState = experimentsStore.getState()
): ExperimentTreatmentType<E> => {
  const treatment = selectExperimentTreatment(state, experiment);

  if (selectShouldReportExperimentTreatedEvent(state, experiment)) {
    reportEcsExperimentTreatment(
      experiment,
      treatment,
      experimentConfigs[experiment].projectTeam,
      state.ecsConfig.etag
    );
  }

  return treatment;
};

export type ReportExperimentTreatment = (experiment: Experiment) => void;

export const reportExperimentTreatment: ReportExperimentTreatment = (experiment) => {
  const snapshot = selectExperimentTreatmentReportingSnapshot(experimentsStore.getState(), experiment);
  if (snapshot.overrideTreatment !== undefined) {
    return;
  }

  reportExperimentTreatmentFromSnapshot(experiment, snapshot);
};

export type ReportExperimentTreatmentNow = (experiment: Experiment) => void;

export const reportExperimentTreatmentNow: ReportExperimentTreatmentNow = (experiment) => {
  const { serviceTreatment, overrideTreatment, etag } = selectExperimentTreatmentReportingSnapshot(
    experimentsStore.getState(),
    experiment
  );

  if (serviceTreatment === undefined || overrideTreatment !== undefined) {
    return;
  }

  reportEcsExperimentTreatmentNow(experiment, serviceTreatment, experimentConfigs[experiment].projectTeam, etag);
};

export const resetReportedExperimentTreatmentsForTesting = (): void => {
  reportedExperimentTreatments.clear();
};
